import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState, ChangeEvent, FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ulid } from 'ulidx';
import { EventRow } from '@/components/guest-list-campaign/EventRow';
import { CampaignConfirmation } from '@/components/guest-list-campaign/Confirmation';
import {
  CAMPAIGN_SUBMITTED_STORAGE_KEY,
  type CampaignConfig,
  type SubmittedCampaignRequest,
  type ValidationErrors,
} from '@/components/guest-list-campaign/types';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  requestedTrough: string;
  message: string;
}

const emptyForm: FormData = { firstName: '', lastName: '', email: '', requestedTrough: '', message: '' };

const confirmationStyles = `
@keyframes gl-fade-up {
  0%   { transform: translateY(8px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
}
.gl-fade-up { animation: gl-fade-up 400ms ease-out both; }
`;

const inputClass =
  'bg-black/50 border-white/10 focus:border-white/30 focus:ring-white/20 mt-1.5 sm:mt-2 text-sm sm:text-base';

export const Route = createFileRoute('/request-guestlists/$campaign-id')({
  component: GuestListCampaignRequestForm,
});

function GuestListCampaignRequestForm() {
  const { toast } = useToast();
  const { 'campaign-id': campaignId } = Route.useParams();
  const [campaign, setCampaign] = useState<CampaignConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Minted once per SUBMISSION, not per click: a retry after a network error
  // sends the SAME id, and the api answers it with the request it already made
  // instead of making a second one. "Submit another request" mints a new one —
  // with the old id the api would replay the first request and silently drop
  // the second (QA, round 3).
  const [submissionId, setSubmissionId] = useState(() => ulid());
  const [error, setError] = useState<string | null>(null);
  // A failed SUBMIT must not replace the form (that is what `error` does, for a
  // failed config load): the requester keeps the form and the same submission
  // id, so pressing the button again is an idempotent retry, not a new request.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormData>(emptyForm);
  // eventId -> places; presence of the key means the event is checked.
  const [selection, setSelection] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState<SubmittedCampaignRequest | null>(null);

  const selectedEvents = useMemo(
    () => (campaign?.events ?? []).filter((e) => selection[e.eventId] !== undefined),
    [campaign, selection],
  );
  const totalPlaces = selectedEvents.reduce((sum, e) => sum + selection[e.eventId], 0);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(CAMPAIGN_SUBMITTED_STORAGE_KEY(campaignId));
      if (raw) setSubmitted(JSON.parse(raw) as SubmittedCampaignRequest);
    } catch {
      /* ignore */
    }
    fetchCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const fetchCampaign = async (): Promise<void> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/guest-list-campaign/${campaignId}`, {
        headers: { 'Accept': 'application/json' },
      });
      if (response.status === 404) {
        setCampaign(null);
        return;
      }
      const data = await response.json();
      setCampaign(data.data);
    } catch (error) {
      console.error('Failed to fetch campaign config:', error);
      setError('Failed to load form configuration');
    } finally {
      setLoading(false);
    }
  };

  const toggleEvent = (eventId: string) => {
    setSelection((prev) => {
      const next = { ...prev };
      if (next[eventId] === undefined) next[eventId] = 1;
      else delete next[eventId];
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setValidationErrors({});
    setSubmitError(null);
    setSubmitting(true);

    const items = selectedEvents.map((ev) => ({ eventId: ev.eventId, places: selection[ev.eventId] }));

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/guest-list-campaign/${campaignId}/requests`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            submission_id: submissionId,
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            requestedTrough: formData.requestedTrough,
            message: formData.message,
            items,
          }),
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 422) {
          setValidationErrors(errorData.errors || {});
          return;
        }
        throw new Error('Failed to submit form');
      }

      const record: SubmittedCampaignRequest = {
        submittedAt: new Date().toISOString(),
        ...formData,
        items: selectedEvents.map((ev) => ({
          eventId: ev.eventId,
          eventName: ev.name,
          places: selection[ev.eventId],
        })),
      };

      try {
        localStorage.setItem(CAMPAIGN_SUBMITTED_STORAGE_KEY(campaignId), JSON.stringify(record));
      } catch {
        /* ignore */
      }

      setSubmitted(record);
      setFormData(emptyForm);
      setSelection({});
    } catch (error) {
      console.error('Failed to submit form:', error);
      setSubmitError('Your request could not be sent. Your answers are still here: press Submit Request to try again.');
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to submit form. Please try again.",
        className: "bg-red-900/50 border-red-500/30 text-white backdrop-blur-sm",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForAnother = () => {
    try {
      localStorage.removeItem(CAMPAIGN_SUBMITTED_STORAGE_KEY(campaignId));
    } catch {
      /* ignore */
    }
    setSubmitted(null);
    setSubmissionId(ulid());
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, field: keyof FormData) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="animate-pulse text-white/80">Loading...</div>
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-red-400">{error}</div>
    </div>
  );
  if (!campaign) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-white/80">Form not found</div>
    </div>
  );

  // Errors that have no input of their own: items, form, and anything unmatched.
  const eventIds = new Set(campaign.events.map((ev) => `events.${ev.eventId}`));
  const handledKeys = new Set(['first_name', 'last_name', 'email', 'requestedTrough', 'message', ...eventIds]);
  const calloutErrors = Object.entries(validationErrors)
    .filter(([key]) => !handledKeys.has(key))
    .flatMap(([, messages]) => messages);
  const hints = [
    campaign.maxPlacesPerEvent !== null && `max ${campaign.maxPlacesPerEvent} per event`,
    campaign.maxPlacesTotal !== null && `max ${campaign.maxPlacesTotal} in total`,
  ].filter(Boolean);
  // Said the moment the picks add up past the total, not only after a submit; the
  // api enforces the same limit and words it the same way.
  const overTotal = campaign !== null && campaign.maxPlacesTotal !== null && totalPlaces > campaign.maxPlacesTotal;
  const canSubmit = selectedEvents.length > 0 && !overTotal && formData.firstName.trim() !== '' && formData.lastName.trim() !== '' && formData.email.trim() !== '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-zinc-900 to-black text-white">
      <style>{confirmationStyles}</style>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-xs sm:text-sm font-medium text-white/70 uppercase tracking-[0.2em] mb-2 sm:mb-3">Guest List Request</h2>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4 text-white">
            {campaign.name}
          </h1>

          {!submitted && campaign.instructions && (
            <div className="mb-6 sm:mb-8 prose prose-invert max-w-none prose-sm sm:prose-base">
              <ReactMarkdown>{campaign.instructions}</ReactMarkdown>
            </div>
          )}
        </div>

        {submitted ? (
          <CampaignConfirmation submitted={submitted} onReset={handleResetForAnother} />
        ) : (
          <div className="relative">
            {campaign.disabled && (
              <div className="absolute inset-0 bg-black/80 backdrop-blur-sm z-10 rounded-xl sm:rounded-2xl flex items-center justify-center">
                <div className="bg-red-900/30 border border-red-500/30 text-red-200 px-4 sm:px-6 py-3 sm:py-4 rounded-lg backdrop-blur-sm max-w-md mx-4 text-center text-sm sm:text-base">
                  {campaign.disabledReason}
                </div>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className={`space-y-6 sm:space-y-8 backdrop-blur-sm bg-white/5 p-4 sm:p-6 md:p-8 rounded-xl sm:rounded-2xl border border-white/10 ${
                campaign.disabled ? 'opacity-50' : ''
              }`}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-4">
                <div>
                  <Label htmlFor="firstName" className="text-white/80 text-sm sm:text-base">First name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange(e, 'firstName')}
                    className={`${inputClass} ${validationErrors.first_name ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.first_name && (
                    <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.first_name[0]}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-white/80 text-sm sm:text-base">Last name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange(e, 'lastName')}
                    className={`${inputClass} ${validationErrors.last_name ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.last_name && (
                    <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.last_name[0]}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-white/80 text-sm sm:text-base">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange(e, 'email')}
                  className={`${inputClass} ${validationErrors.email ? 'border-red-500' : ''}`}
                />
                {validationErrors.email && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.email[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-white/80 text-sm sm:text-base">Events</Label>
                {campaign.events.map((ev) => (
                  <EventRow
                    key={ev.eventId}
                    event={ev}
                    places={selection[ev.eventId]}
                    error={validationErrors[`events.${ev.eventId}`]?.[0]}
                    onToggle={() => toggleEvent(ev.eventId)}
                    onPlacesChange={(value) => setSelection({ ...selection, [ev.eventId]: value })}
                  />
                ))}
                <div className="flex items-center justify-between pt-1 text-xs sm:text-sm text-white/60">
                  <span>{hints.join(' · ')}</span>
                  <span className="text-white/80 font-medium">Total: {totalPlaces} {totalPlaces === 1 ? 'place' : 'places'}</span>
                </div>
                {overTotal && campaign && (
                  <div
                    className="bg-red-900/30 border border-red-500/30 text-red-200 px-3 sm:px-4 py-2.5 rounded-lg text-sm"
                    role="alert"
                    data-testid="guestlist-campaign-over-total"
                  >
                    You can request at most {campaign.maxPlacesTotal} places in total across all events ({totalPlaces} requested).
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="requestedTrough" className="text-white/80 text-sm sm:text-base">Referred by</Label>
                <Input
                  id="requestedTrough"
                  value={formData.requestedTrough}
                  onChange={(e) => handleInputChange(e, 'requestedTrough')}
                  className={`${inputClass} ${validationErrors.requestedTrough ? 'border-red-500' : ''}`}
                />
                {validationErrors.requestedTrough && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.requestedTrough[0]}</p>
                )}
              </div>

              <div>
                <Label htmlFor="message" className="text-white/80 text-sm sm:text-base">Additional Notes (optional)</Label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange(e, 'message')}
                  className={`w-full min-h-[80px] sm:min-h-[100px] px-3 sm:px-4 py-2 sm:py-3 mt-1.5 sm:mt-2 bg-black/50 border rounded-lg focus:ring-white/20 focus:border-white/30 text-sm sm:text-base ${
                    validationErrors.message ? 'border-red-500' : 'border-white/10'
                  }`}
                />
                {validationErrors.message && (
                  <p className="text-red-400 text-xs sm:text-sm mt-1">{validationErrors.message[0]}</p>
                )}
              </div>

              {calloutErrors.length > 0 && (
                <div className="bg-red-900/30 border border-red-500/30 text-red-200 px-3 sm:px-4 py-2.5 rounded-lg text-sm space-y-1">
                  {calloutErrors.map((message, i) => (
                    <p key={i}>{message}</p>
                  ))}
                </div>
              )}

              {submitError && (
                <div className="bg-red-900/30 border border-red-500/30 text-red-200 px-3 sm:px-4 py-2.5 rounded-lg text-sm" role="alert">
                  {submitError}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting || !canSubmit}
                className="w-full bg-white hover:bg-white/90 text-black font-semibold py-4 sm:py-6 rounded-lg transition-all duration-300 text-sm sm:text-base"
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 sm:mr-3 h-4 w-4 sm:h-5 sm:w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Request...
                  </>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </form>
          </div>
        )}

        <div className="mt-8 sm:mt-16 pt-4 sm:pt-8 border-t border-white/10 text-center">
          <a href="https://fastlane.events" target="_blank" rel="noopener noreferrer" className="inline-block opacity-75 hover:opacity-100 transition-opacity">
            <p className="text-xs sm:text-sm text-white/60 mb-2 sm:mb-3">Powered by Fastlane</p>
          </a>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
